import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import { THEME_MESSAGES } from 'utils/constants';
import { getIsNotFound } from 'utils/getIsNotFound';
import { getPaymentDiscrepancyLog } from 'api/admin/paymentDiscrepancyLogs/paymentDiscrepancyLogs';
import { ApiError } from 'types/api';
import { PaymentDiscrepancyLogListItem } from './usePaymentDiscrepancyLogs';

export interface PaymentDiscrepancyRefund {
  id: number;
  refundAmountInCents: number;
  status: string;
  reason: string;
  createdAt: string;
  refundProvider: string | null;
  thirdPartyRefundId: string | null;
}

export interface PaymentDiscrepancyPayment {
  id: number;
  status: string;
  paymentProcessor: string;
  paymentToken: string | null;
  totalAmount: number;
  orderPaymentAmount: number | null;
  totalRefunded: number;
  isFullyRefunded: boolean;
  createdAt: string;
  refunds: PaymentDiscrepancyRefund[];
}

export interface PaymentDiscrepancyLogDetail extends PaymentDiscrepancyLogListItem {
  orderPaymentId: number | null;
  orderId: number;
  paymentsAndRefunds: PaymentDiscrepancyPayment[];
}

interface GetPaymentDiscrepancyLogOutput {
  success: boolean;
  paymentDiscrepancyLog: PaymentDiscrepancyLogDetail;
}

export const usePaymentDiscrepancyLog = (id: string | number | undefined) => {
  return useQuery<unknown, ApiError, PaymentDiscrepancyLogDetail>({
    queryKey: ['paymentDiscrepancyLog', id],
    queryFn: async () => {
      const { data } = (await getPaymentDiscrepancyLog(
        id,
      )) as AxiosResponse<GetPaymentDiscrepancyLogOutput>;

      return data.paymentDiscrepancyLog;
    },
    onError: (error) => {
      Sentry.captureException(error);

      if (!getIsNotFound(error)) {
        toast.error(error?.response?.data?.error ?? THEME_MESSAGES.somethingWrong, {
          duration: 4000,
        });
      }
    },
    enabled: !!id,
  });
};
